const errorMessage = (message) => {
  return `"editor-platform-transport": ${message}`;
};
const internalErrorMessage = (message) => {
  return errorMessage(`[Internal Error] ${message}`);
};

const FUNCTION_MARKER = "\u0192";
const ACCESSOR_MARKER = "\u2202";
const ACCESSOR_GET_POINTER = "_get_";
const ACCESSOR_SET_POINTER = "_set_";
const WITH_ACCESSOR_INDICATOR = "+";
const NO_ACCESSOR_INDICATOR = "-";
const PATH_SEPARATOR = "\xB7";
const ENCODER_MARKER = "\xD7";
const ENCODER_EMPTY_MARKER = "\xB0";
const extractPointer = (pointer) => pointer[0] === ENCODER_MARKER ? "" : pointer;
const isFinalNode = (instance) => {
  return instance === null || typeof instance === "undefined" || typeof instance === "symbol" || typeof instance === "number" || typeof instance === "boolean" || typeof instance === "function" || typeof instance === "string";
};
const isSerializableType = (instance) => {
  return !!instance && typeof instance === "object" && !Array.isArray(instance) && !(instance instanceof Date) && !(instance instanceof Set) && !(instance instanceof Map);
};
const beautifyPath = (path) => beautifyBreadcrumbs(path.split(PATH_SEPARATOR));
const beautifyBreadcrumbs = (breadcrumbs) => breadcrumbs.map(extractPointer).filter(Boolean).join(".");

function createStructureEncoder(marker, config) {
  return {
    marker,
    ...config
  };
}
const arrayEncoder = createStructureEncoder("A", {
  shouldUse: (instance) => Array.isArray(instance),
  serialize: (instance, next) => {
    if (!instance.length) {
      next("", ENCODER_EMPTY_MARKER);
    } else {
      instance.forEach((value, index) => {
        next(`${index}`, value);
      });
    }
  },
  deserialize: {
    create: () => [],
    has: (instance, key) => !(typeof instance[key] === "undefined"),
    append: (instance, _key, value) => {
      instance.push(value);
    }
  }
});
const mapEncoder = createStructureEncoder("M", {
  shouldUse: (instance) => instance instanceof Map,
  serialize: (instance, next) => {
    instance.forEach((value, key) => {
      next(key, value);
    });
  },
  deserialize: {
    create: () => /* @__PURE__ */ new Map(),
    has: (instance, key) => instance.has(key),
    append: (instance, key, value) => {
      instance.set(key, value);
    }
  }
});
const setEncoder = createStructureEncoder("S", {
  shouldUse: (instance) => instance instanceof Set,
  serialize: (instance, next) => {
    let index = 0;
    instance.forEach((value) => {
      next(`${index++}`, value);
    });
  },
  deserialize: {
    create: () => /* @__PURE__ */ new Set(),
    has: (instance, key) => instance.has(key),
    append: (instance, _key, value) => {
      instance.add(value);
    }
  }
});
const objectEncoder = createStructureEncoder(
  "O",
  {
    shouldUse: (instance) => typeof instance === "object",
    serialize: (root, next) => {
      const forEachMember = (instance) => {
        const keys = Reflect.ownKeys(instance);
        const members = keys.filter((key) => {
          return key !== "constructor";
        }).filter((key) => {
          return typeof key !== "symbol";
        });
        if (!members.length) {
          next("", ENCODER_EMPTY_MARKER);
        } else {
          members.filter((key) => {
            return key !== "constructor";
          }).filter((key) => {
            return typeof key !== "symbol";
          }).forEach((key) => {
            const value = Reflect.get(root, key);
            next(key, value);
          });
        }
        const parent = Reflect.getPrototypeOf(instance);
        if (!parent || parent?.hasOwnProperty("__proto__")) {
          return;
        }
        forEachMember(parent);
      };
      forEachMember(root);
    },
    deserialize: {
      create: () => ({}),
      has: (instance, key) => !!instance[key],
      append: (instance, key, value) => {
        instance[key] = value;
      }
    }
  }
);

const encoders = [arrayEncoder, setEncoder, mapEncoder];
function getDescriptor(obj, key) {
  let _instance = obj;
  while (_instance) {
    const descriptor = Object.getOwnPropertyDescriptor(_instance, key);
    if (descriptor) {
      return descriptor;
    }
    _instance = Reflect.getPrototypeOf(_instance);
  }
  return null;
}
function forEachMember(root, callback) {
  const ancestors = /* @__PURE__ */ new WeakMap();
  const getContextPointer = (breadcrumbs) => {
    const _breadcrumbs = breadcrumbs.slice(1).map(extractPointer).filter(Boolean);
    return _breadcrumbs.slice(1).reduce(
      (acc, pointer) => {
        return {
          context: acc.context[acc.pointer],
          pointer
        };
      },
      {
        context: root,
        pointer: _breadcrumbs[0]
      }
    );
  };
  const iterateStructure = (instance, breadcrumbs = []) => {
    if (ancestors.has(instance)) {
      const hasDuplicates = ancestors.get(instance)?.some((p) => breadcrumbs.join(".").startsWith(p));
      if (hasDuplicates) {
        callback(breadcrumbs, {
          ...getContextPointer(breadcrumbs),
          value: "[Circular]"
        });
        return;
      }
    }
    if (instance && !isFinalNode(instance)) {
      if (ancestors.has(instance)) {
        ancestors.set(instance, []);
      }
      ancestors.set(instance, [
        ...ancestors.get(instance) ?? [],
        breadcrumbs.join(".")
      ]);
    }
    if (isFinalNode(instance)) {
      callback(breadcrumbs, {
        ...getContextPointer(breadcrumbs),
        value: instance
      });
      return;
    }
    const encoder = encoders.find((_encoder) => _encoder.shouldUse(instance)) ?? objectEncoder;
    if (encoder) {
      encoder.serialize(instance, (key, value) => {
        iterateStructure(value, [
          ...breadcrumbs,
          `${ENCODER_MARKER}${encoder.marker}`,
          key
        ]);
      });
      return;
    }
  };
  return iterateStructure(root);
}
const serialize = (input, registerFunctionMarker) => {
  if (!isSerializableType(input)) {
    throw new Error(
      errorMessage("only objects are serializable at the root level")
    );
  }
  const buffer = {
    data: []
  };
  const appendValue = (path, value) => {
    buffer.data.push([path, value]);
  };
  const appendFunction = (path, config) => {
    registerFunctionMarker(beautifyPath(path), (...args) => {
      return config.context[config.pointer](...args);
    });
    appendValue(path, FUNCTION_MARKER);
  };
  forEachMember(input, (breadcrumbs, { value, context, pointer }) => {
    const path = breadcrumbs.slice(1).join(PATH_SEPARATOR);
    const descriptor = getDescriptor(context, pointer);
    if (descriptor?.get || descriptor?.set) {
      if (descriptor?.get) {
        registerFunctionMarker(
          beautifyBreadcrumbs([...breadcrumbs, ACCESSOR_GET_POINTER]),
          () => context[pointer]
        );
      }
      if (descriptor?.set) {
        registerFunctionMarker(
          beautifyBreadcrumbs([...breadcrumbs, ACCESSOR_SET_POINTER]),
          (_value) => {
            context[pointer] = _value;
          }
        );
      }
      const marker = [
        descriptor?.get ? WITH_ACCESSOR_INDICATOR : NO_ACCESSOR_INDICATOR,
        descriptor?.set ? WITH_ACCESSOR_INDICATOR : NO_ACCESSOR_INDICATOR
      ];
      appendValue(path, `${ACCESSOR_MARKER}${marker.join("")}`);
    } else if (typeof value === "function") {
      appendFunction(path, { context, pointer });
    } else {
      appendValue(path, value);
    }
  });
  return buffer;
};
const deserialize = (input, replaceFunctionMarker) => {
  if (typeof input !== "object" || input === null) {
    return input;
  }
  return input.data.reduce(
    (acc, [breadcrumbs, value]) => {
      let pointer = acc;
      const pointers = breadcrumbs.split(PATH_SEPARATOR);
      let encoder = objectEncoder;
      let key = null;
      for (let i = 0; i < pointers.length; i++) {
        const point = pointers[i];
        if (point[0] === ENCODER_MARKER) {
          const has = encoder.deserialize.has(pointer, key);
          encoder = [objectEncoder, ...encoders].find(
            (_iterator) => _iterator.marker === point[1]
          );
          if (!encoder) {
            throw new Error(errorMessage(`unknown marker "${point[1]}"`));
          }
          if (!has) {
            pointer[key] = encoder.deserialize.create();
          }
          pointer = pointer[key];
        } else {
          key = point;
        }
      }
      if (value?.toString() === ENCODER_EMPTY_MARKER) {
        return acc;
      } else if (value?.toString() === FUNCTION_MARKER) {
        encoder.deserialize.append(
          pointer,
          key,
          replaceFunctionMarker(beautifyBreadcrumbs(pointers))
        );
      } else if (value?.toString()[0] === ACCESSOR_MARKER) {
        const hasGetter = value?.toString()[1] === WITH_ACCESSOR_INDICATOR;
        const hasSetter = value?.toString()[2] === WITH_ACCESSOR_INDICATOR;
        const descriptor = {};
        if (hasGetter) {
          const getter = replaceFunctionMarker(
            beautifyBreadcrumbs([...pointers, ACCESSOR_GET_POINTER])
          );
          descriptor.get = () => getter();
        }
        if (hasSetter) {
          descriptor.set = replaceFunctionMarker(
            beautifyBreadcrumbs([...pointers, ACCESSOR_SET_POINTER])
          );
        }
        Object.defineProperty(pointer, key, descriptor);
      } else {
        encoder.deserialize.append(pointer, key, value);
      }
      return acc;
    },
    // TODO: define as enumrable
    {
      [Symbol("transferred object")]: `${(JSON.stringify(input.data).length / 1024).toFixed(2)}Kb`
    }
  );
};

var MessageType = /* @__PURE__ */ ((MessageType2) => {
  MessageType2["ExposeAPI"] = "ExposeAPI";
  MessageType2["Call"] = "Call";
  MessageType2["Response"] = "Response";
  MessageType2["Cleanup"] = "Cleanup";
  return MessageType2;
})(MessageType || {});
class Message {
  constructor(type, payload) {
    this.type = type;
    this.payload = payload;
  }
  correlationId = `M-${performance.now()}`;
  parentCorrelationId;
  withParentCorrelationId(correlationId) {
    this.parentCorrelationId = correlationId;
    return this;
  }
  serialize(registerFunctionMarker) {
    return JSON.stringify({
      parentCorrelationId: this.parentCorrelationId,
      correlationId: this.correlationId,
      type: this.type,
      payload: serialize(this.payload, (path, value) => {
        registerFunctionMarker(this.correlationId, path, value);
      })
    });
  }
  static fromEvent(data, replaceFunctionMarker) {
    const parsed = JSON.parse(data);
    return new Message(
      parsed.type,
      deserialize(parsed.payload, (breadcrumbs) => {
        return replaceFunctionMarker(parsed.correlationId, breadcrumbs);
      })
    ).withParentCorrelationId(parsed.correlationId);
  }
}
class MessageCleanup extends Message {
  constructor(payload) {
    super("Cleanup" /* Cleanup */, payload);
  }
}
class MessageResponse extends Message {
  constructor(payload) {
    super("Response" /* Response */, payload);
  }
}
class MessageResponseError extends Message {
  constructor(payload) {
    super("Response" /* Response */, payload);
  }
}
class MessageCall extends Message {
  constructor(payload) {
    super("Call" /* Call */, payload);
  }
}

const buildFunctionMarkerKey = (correlationId, path) => {
  return `${correlationId}_${path}`;
};
class ThreadChannel {
  constructor(port) {
    this.port = port;
    this.port.onmessage = (event) => {
      this.onMessage(event);
    };
  }
  #registry = new FinalizationRegistry((params) => {
    this.postMessage(new MessageCleanup(params));
  });
  #messageEvents = {
    [MessageType.Cleanup]: (message) => {
      const functionMarkerKey = message.payload.functionMarkerKey;
      const callId = message.payload.callId;
      delete this.#functionMarkers[functionMarkerKey];
      if (callId) {
        delete this.#pendingFunctionResponse[callId];
      }
    },
    /**
     * Return function response between threads
     */
    [MessageType.Response]: async (message) => {
      const callId = message.payload.callId;
      const _errorMessage = message.payload.errorMessage;
      if (_errorMessage) {
        const errorStack = message.payload.errorStack;
        if (errorStack) {
          const error = new Error(_errorMessage);
          error.stack = message.payload.errorStack;
          this.#pendingFunctionResponse[callId]?.reject(error);
        } else {
          this.#pendingFunctionResponse[callId]?.reject(_errorMessage);
        }
      } else {
        const response = message.payload.response;
        this.#pendingFunctionResponse[callId]?.resolve(response);
      }
      delete this.#pendingFunctionResponse[callId];
    },
    /**
     * Calling function between threads
     */
    [MessageType.Call]: async (message) => {
      const functionMarkerKey = message.payload.functionMarkerKey;
      const marker = this.#functionMarkers[functionMarkerKey];
      if (!marker) {
        this.postMessage(
          new MessageResponseError({
            callId: message.payload.callId,
            errorMessage: errorMessage("function is not available")
          })
        );
        return;
      }
      try {
        const response = await marker.value(...message.payload.props ?? []);
        this.postMessage(
          new MessageResponse({
            callId: message.payload.callId,
            response
          })
        );
      } catch (error) {
        this.postMessage(
          new MessageResponseError({
            callId: message.payload.callId,
            /**
             * for better DX we need to pass Error object to keep stack trace available
             * but in some cases our custom error messages are not serialized correctly
             */
            errorMessage: error.message,
            errorStack: error.stack
          })
        );
      }
    }
  };
  #functionMarkers = {};
  #pendingFunctionResponse = {};
  queue = [];
  onMessage(event) {
    const message = Message.fromEvent(event.data, (correlationId, path) => {
      const replacedFunction = this.replaceTransferredFunction(
        correlationId,
        path
      );
      this.#registry.register(replacedFunction, {
        functionMarkerKey: buildFunctionMarkerKey(correlationId, path)
        // callId: message.payload?.callId,
      });
      return replacedFunction;
    });
    this.#messageEvents[message.type]?.(message);
    this.messageEvents[message.type]?.(message);
  }
  postMessage(message) {
    this.port.postMessage(
      message.serialize(this.registerTransferredFunction.bind(this))
    );
  }
  registerTransferredFunction(correlationId, path, value) {
    const functionMarkerKey = buildFunctionMarkerKey(correlationId, path);
    this.#functionMarkers[functionMarkerKey] = {
      path,
      value
    };
  }
  replaceTransferredFunction(correlationId, path) {
    return (...props) => {
      const callId = `CALL-${crypto.randomUUID()}`;
      this.postMessage(
        new MessageCall({
          callId,
          functionMarkerKey: buildFunctionMarkerKey(correlationId, path),
          props
        })
      );
      return new Promise((resolve, reject) => {
        this.#pendingFunctionResponse[callId] = { resolve, reject };
      });
    };
  }
  flush() {
    this.queue.forEach((message) => {
      this.postMessage(message);
    });
    this.queue = [];
  }
  /**
   * TODO:
   * before message port is disposed
   * we need to cleanup all pending promises (probably reject or cancel with AbortController)
   * note, this function should be async, and we need
   * to wait it before dispose port and create a new port
   */
  // shutdown() {
  //   this.postMessage(new MessageShutdown());
  //
  //   Object.keys(this.#pendingFunctionResponse).forEach((key) => {
  //     const value = this.#pendingFunctionResponse[key]
  //     if (value && value.reject && value.resolve) {
  //       value.reject(new Error('transport port shutdown'));
  //     }
  //   })
  // }
}

class ConsumerChannel extends ThreadChannel {
  messageEvents = {};
  expose(instance) {
    if (!instance.type) {
      throw new Error(
        errorMessage('wrong exposed API instance, "type" property is required')
      );
    }
    this.queue.push(new Message(MessageType.ExposeAPI, instance));
    this.flush();
  }
}

class HostChannel extends ThreadChannel {
  #consumerAPIInstances = {};
  #consumerAPIInstancesResolvers = [];
  messageEvents = {
    [MessageType.ExposeAPI]: (message) => {
      const pendingTypeInstances = this.#consumerAPIInstancesResolvers.filter(
        ({ type }) => type === message.payload.type
      );
      this.#consumerAPIInstancesResolvers = this.#consumerAPIInstancesResolvers.filter(
        ({ type }) => type !== message.payload.type
      );
      pendingTypeInstances.forEach(({ cb }) => {
        cb(message.payload);
      });
    }
  };
  waitAPI(type) {
    return new Promise((resolve) => {
      this.onAPICallback(type, (_workerApi) => {
        resolve(_workerApi);
      });
    });
  }
  onAPICallback(type, cb) {
    if (this.#consumerAPIInstances[type]) {
      cb(this.#consumerAPIInstances[type]);
      return;
    }
    this.#consumerAPIInstancesResolvers.push({
      type,
      cb
    });
  }
}

var HandshakeEvent = /* @__PURE__ */ ((HandshakeEvent2) => {
  HandshakeEvent2["Ping"] = "EDITOR_PLATFORM_TRANSPORT_EVENT:__Ping";
  HandshakeEvent2["RequestPort"] = "EDITOR_PLATFORM_TRANSPORT_EVENT:__RequestPort";
  HandshakeEvent2["Pong"] = "EDITOR_PLATFORM_TRANSPORT_EVENT:__Pong";
  HandshakeEvent2["Port"] = "EDITOR_PLATFORM_TRANSPORT_EVENT:__Port";
  return HandshakeEvent2;
})(HandshakeEvent || {});

class AbstractHandshake {
  constructor(postMessage, onMessage) {
    this.postMessage = postMessage;
    this.onMessage = onMessage;
  }
  #listeners = /* @__PURE__ */ new Set();
  #activePort;
  #notifyHandshakeListeners() {
    const port = this.#activePort;
    if (!port) {
      console.warn(
        internalErrorMessage(
          "port is required no notify onHandshake listeners"
        )
      );
      return;
    }
    this.#listeners.forEach((cb) => cb(port));
  }
  setActivePort(port) {
    this.dispose();
    this.#activePort = port;
    this.#notifyHandshakeListeners();
  }
  dispose() {
    if (this.#activePort) {
      this.#activePort.close();
      this.#activePort.onmessage = null;
    }
  }
  onHandshake(callback) {
    this.#listeners.add(callback);
    return () => {
      this.#listeners.delete(callback);
    };
  }
}

class HostHandshake extends AbstractHandshake {
  #messageChannel;
  constructor(postMessage, onMessage) {
    super(postMessage, onMessage);
    this.#initHandshake();
    this.#onConsumerPing();
  }
  #initHandshake() {
    this.onMessage((event) => {
      try {
        const payload = JSON.parse(event.data);
        switch (payload.type) {
          case HandshakeEvent.Ping: {
            this.#onConsumerPing();
            break;
          }
          case HandshakeEvent.RequestPort: {
            this.#onConsumerRequestPort(payload.memberId);
            break;
          }
        }
      } catch (e) {
      }
    });
  }
  #onConsumerPing() {
    const payload = {
      type: HandshakeEvent.Pong
    };
    this.postMessage(JSON.stringify(payload));
  }
  #onConsumerRequestPort(memberId) {
    const messageChannel = this.#createMessageChannel();
    this.setActivePort(messageChannel?.port1);
    const payload = {
      type: HandshakeEvent.Port,
      targetMemberId: memberId
    };
    this.postMessage(JSON.stringify(payload), messageChannel.port2);
  }
  #createMessageChannel() {
    if (this.#messageChannel) {
      this.#messageChannel.port1.close();
      this.#messageChannel.port1.onmessage = null;
    }
    this.#messageChannel = new MessageChannel();
    return this.#messageChannel;
  }
}

class ConsumerHandshake extends AbstractHandshake {
  #hostIsAlive = false;
  #memberId = `consumer-${performance.now()}`;
  constructor(postMessage, onMessage) {
    super(postMessage, onMessage);
    this.#initHandshake();
    const payload = {
      type: HandshakeEvent.Ping
    };
    postMessage(JSON.stringify(payload));
  }
  #initHandshake() {
    this.onMessage((event) => {
      try {
        const payload = JSON.parse(event.data);
        switch (payload.type) {
          case HandshakeEvent.Pong: {
            this.#onHostPing();
            break;
          }
          case HandshakeEvent.Port: {
            this.#onHostPort(payload.targetMemberId, event.ports?.[0]);
            break;
          }
        }
      } catch (e) {
      }
    });
  }
  #onHostPing() {
    if (this.#hostIsAlive) {
      return;
    }
    this.#hostIsAlive = true;
    const payload = {
      type: HandshakeEvent.RequestPort,
      memberId: this.#memberId
    };
    this.postMessage(JSON.stringify(payload));
  }
  #onHostPort(targetMemberId, port) {
    if (targetMemberId !== this.#memberId) {
      return;
    }
    if (!port) {
      throw new Error(internalErrorMessage("no port at consumer handshake"));
    }
    this.setActivePort(port);
  }
}

class AbstractHostEndpoint {
  get onConnect() {
    return this.handshake.onHandshake.bind(this.handshake);
  }
}
class AbstractConsumerEndpoint {
  get onConnect() {
    return this.handshake.onHandshake.bind(this.handshake);
  }
}

class IFrameConsumerEndpoint extends AbstractConsumerEndpoint {
  static exposeAPI(instance) {
    const consumer = new IFrameConsumerEndpoint();
    consumer.onConnect((port) => {
      const transport = new ConsumerChannel(port);
      transport.expose(instance);
    });
  }
  handshake = new ConsumerHandshake(
    (message) => {
      globalThis.parent.postMessage(message, "*");
    },
    (listener) => {
      globalThis.addEventListener("message", listener);
    }
  );
}

class IFrameHostEndpoint extends AbstractHostEndpoint {
  constructor(iframe) {
    super();
    this.iframe = iframe;
    this.handshake = new HostHandshake(
      (message, port) => {
        if (port) {
          this.iframe.contentWindow?.postMessage(message, "*", [port]);
        } else {
          this.iframe.contentWindow?.postMessage(message, "*");
        }
      },
      (listener) => {
        globalThis.addEventListener("message", listener);
      }
    );
  }
  static connect(iframe, cb) {
    const host = new IFrameHostEndpoint(iframe);
    host.onConnect((port) => {
      const instance = new HostChannel(port);
      cb(instance);
    });
  }
  handshake;
}

class WorkerConsumerEndpoint extends AbstractConsumerEndpoint {
  static exposeAPI(instance) {
    const consumer = new WorkerConsumerEndpoint();
    consumer.onConnect((port) => {
      const transport = new ConsumerChannel(port);
      transport.expose(instance);
    });
  }
  handshake = new ConsumerHandshake(
    (msg) => {
      globalThis.postMessage(msg);
    },
    (listener) => {
      globalThis.addEventListener("message", listener);
    }
  );
}

class WorkerHostEndpoint extends AbstractHostEndpoint {
  constructor(worker) {
    super();
    this.worker = worker;
    this.handshake = new HostHandshake(
      (message, port) => {
        if (port) {
          this.worker.postMessage(message, [port]);
        } else {
          this.worker.postMessage(message);
        }
      },
      (listener) => {
        this.worker.addEventListener("message", listener);
      }
    );
  }
  static connect(worker, cb) {
    const host = new WorkerHostEndpoint(worker);
    host.onConnect((port) => {
      const instance = new HostChannel(port);
      cb(instance);
    });
  }
  handshake;
}

export { ConsumerChannel, HostChannel, IFrameConsumerEndpoint, IFrameHostEndpoint, WorkerConsumerEndpoint, WorkerHostEndpoint };
//# sourceMappingURL=index.js.map
